import React, { useState, useEffect } from "react";
import axios from "axios";
// import NavigationPaperDashboard from "../navbar/Navbar";
import NavigationPaperDashboard from "../navbar/NavbarPaperDashboard";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Alert from "../Alert/Alert";
import API_ENDPOINTS from "../../config/config";
import "./paperOut.css";
import "./paperOutPage.css";

const PaperOutPage = () => {
  // Helper function to get local date in YYYY-MM-DD format (not UTC)
  const getLocalDateString = (date = new Date()) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const [activeTab, setActiveTab] = useState("reel-out");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedReel, setSelectedReel] = useState(null);
  const [outQuantity, setOutQuantity] = useState("");
  const [outDate, setOutDate] = useState(getLocalDateString()); // Default to today in local timezone
  const [reels, setReels] = useState([]);
  const [history, setHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(15);

  const [showReelDetails, setShowReelDetails] = useState(false);
  const [filters, setFilters] = useState({
    paperName: "",
    millName: "",
    shadeName: "",
    reelNumber: "",
    minDays: "",
    maxDays: "",
  });

  // History date filters
  const [historyDateFilters, setHistoryDateFilters] = useState({
    startDate: "",
    endDate: "",
  });

  // Calculate current items and total pages
  const getPaginatedData = (items) => {
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = items.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(items.length / itemsPerPage);
    return { currentItems, totalPages };
  };

  // const { currentItems: paginatedReels, totalPages: reelTotalPages } =
  //   getPaginatedData(reels);
  // const { currentItems: paginatedHistory, totalPages: historyTotalPages } =
  //   getPaginatedData(history);

  // Calculate history totals based on filtered data
  const getFilteredHistory = () => {
    if (!historyDateFilters.startDate && !historyDateFilters.endDate) {
      return history;
    }

    return history.filter((entry) => {
      const entryDate = new Date(entry.out_timestamp);
      const startDate = historyDateFilters.startDate
        ? new Date(historyDateFilters.startDate)
        : null;
      const endDate = historyDateFilters.endDate
        ? new Date(new Date(historyDateFilters.endDate).setHours(23, 59, 59, 999))
        : null;

      if (startDate && endDate) {
        return entryDate >= startDate && entryDate <= endDate;
      } else if (startDate) {
        return entryDate >= startDate;
      } else if (endDate) {
        return entryDate <= endDate;
      }
      return true;
    });
  };

  const calculateHistoryTotals = () => {
    const filteredHistory = getFilteredHistory();
    const uniqueReels = new Set();
    
    const totals = filteredHistory.reduce(
      (acc, entry) => {
        uniqueReels.add(entry.reel_number);
        return {
          totalQuantity: acc.totalQuantity + Number(entry.quantity_used),
          totalPrice: acc.totalPrice + Number(entry.quantity_used) * Number(entry.rate_per_kg),
          reelCount: uniqueReels.size
        };
      },
      { totalQuantity: 0, totalPrice: 0, reelCount: 0 }
    );
    
    return totals;
  };

  // Pagination handlers
  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const goToNextPage = () => {
    if (
      currentPage <
      (activeTab === "reel-out" ? reelTotalPages : historyTotalPages)
    ) {
      setCurrentPage(currentPage + 1);
    }
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, searchQuery]);

  // Add this to your useEffect to handle filter changes
  useEffect(() => {
    const debounceSearch = setTimeout(() => {
      if (Object.values(filters).some((value) => value)) {
        searchReels(filters);
      } else {
        fetchReels(); // Fetch all reels if no filters are active
      }
    }, 300);

    return () => clearTimeout(debounceSearch);
  }, [filters]);

  const fetchReels = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const reelsResponse = await axios.get(API_ENDPOINTS.REELS.READ);
      setReels(Array.isArray(reelsResponse.data) ? reelsResponse.data : []);
    } catch (err) {
      setError("Failed to fetch reels. Please try again.");
      console.error("Reels fetch error:", err);
      setReels([]);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchHistory = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const historyResponse = await axios.get(API_ENDPOINTS.REELS.HISTORY);
      setHistory(
        Array.isArray(historyResponse.data) ? historyResponse.data : []
      );
    } catch (err) {
      setError("Failed to fetch history. Please try again.");
      console.error("History fetch error:", err);
      setHistory([]);
    } finally {
      setIsLoading(false);
    }
  };

  const searchReels = async (filters) => {
    setIsLoading(true);
    setError(null);
    try {
      // Create a new URLSearchParams object
      const queryParams = new URLSearchParams();

      // Process each filter value
      Object.entries(filters).forEach(([key, value]) => {
        if (value) {
          // For millName, shadeName, and reelNumber, send the partial value directly
          if (["millName", "shadeName", "reelNumber"].includes(key)) {
            queryParams.append(key, value);
          } else {
            // For other fields, keep the existing behavior
            queryParams.append(key, value);
          }
        }
      });

      const searchResponse = await axios.get(
        `${API_ENDPOINTS.REELS.SEARCH}?${queryParams.toString()}`
      );

      setReels(Array.isArray(searchResponse.data) ? searchResponse.data : []);
    } catch (err) {
      setError("Failed to search reels. Please try again.");
      console.error("Reels search error:", err);
      setReels([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);

    if (window.searchTimeout) {
      clearTimeout(window.searchTimeout);
    }

    window.searchTimeout = setTimeout(() => {
      if (query) {
        searchReels(query);
      } else {
        fetchReels();
      }
    }, 300);
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleHistoryDateFilterChange = (field, value) => {
    setHistoryDateFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
    setCurrentPage(1); // Reset to first page when filter changes
  };

  const resetHistoryDateFilters = () => {
    setHistoryDateFilters({
      startDate: "",
      endDate: "",
    });
    setSortColumn(null);
    setSortDirection("asc");
    setCurrentPage(1);
  };

  const handleReset = () => {
    setFilters({
      paperName: "",
      millName: "",
      shadeName: "",
      reelNumber: "",
      minDays: "",
      maxDays: "",
    });
    setSortColumn(null);
    setSortDirection("asc");
  };

  const handleOutQuantitySubmit = async () => {
    if (!selectedReel || !outQuantity || isNaN(outQuantity)) return;

    // Validate date against reel history (allow same day stock outs)
    const minDate = getMinDateForReel(selectedReel.reel_number);
    console.log('Validation - outDate:', outDate, 'minDate:', minDate, 'comparison:', outDate < minDate);
    
    // Only block if date is EARLIER than last stock out (same day is allowed)
    if (minDate && outDate < minDate) {
      setError(
        `Stock out date cannot be earlier than ${new Date(minDate).toLocaleDateString('en-GB')} (last stock out date for this reel). Same day stock outs are allowed.`
      );
      return;
    }

    // Validate date is not in the future
    const today = getLocalDateString();
    if (outDate > today) {
      setError("Stock out date cannot be in the future.");
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const response = await axios.post(API_ENDPOINTS.REELS.STOCK_OUT, {
        reelNumber: selectedReel.reel_number,
        outQuantity: Number(outQuantity),
        outDate: outDate, // Send the selected date to backend
      });

      if (response.data.success) {
        fetchReels();
        fetchHistory();
        setSuccessMessage(
          `Successfully stocked out ${outQuantity} kg from reel ${selectedReel.reel_number} on ${outDate}`
        );
        setSelectedReel(null);
        setOutQuantity("");
        setOutDate(getLocalDateString()); // Reset to today
      } else {
        setError(response.data.message);
      }
    } catch (err) {
      setError(
        err.response?.data?.message ||
          "Failed to stock out reel. Please try again."
      );
      console.error("Reel out error:", err);
    } finally {
      setIsLoading(false);
    }
  };

  // Get minimum allowed date for stock out based on reel's history
  const getMinDateForReel = (reelNumber) => {
    // Filter history for this specific reel
    const reelHistory = history.filter(entry => entry.reel_number === reelNumber);
    
    if (reelHistory.length === 0) {
      // No history, no minimum date restriction
      return null;
    }
    
    // Find the most recent (maximum) stock out date (date only, ignore time)
    let maxDateString = null;
    reelHistory.forEach(entry => {
      // Extract only the date part (YYYY-MM-DD) from timestamp
      // Handle both MySQL format (YYYY-MM-DD HH:MM:SS) and ISO format (YYYY-MM-DDTHH:MM:SS)
      let dateOnly = entry.out_timestamp;
      if (dateOnly.includes('T')) {
        // ISO format: split by 'T'
        dateOnly = dateOnly.split('T')[0];
      } else {
        // MySQL format: split by space
        dateOnly = dateOnly.split(' ')[0];
      }
      
      if (!maxDateString || dateOnly > maxDateString) {
        maxDateString = dateOnly;
      }
    });
    
    if (!maxDateString) return null;
    
    // Return the same date (same day is allowed for multiple stock outs)
    console.log('getMinDateForReel for', reelNumber, '- returning:', maxDateString);
    return maxDateString;
  };

  const handleReelClick = (reel) => {
    setSelectedReel(reel);
    setShowReelDetails(true);
    
    // Set date to today or minimum allowed date, whichever is later
    const minDate = getMinDateForReel(reel.reel_number);
    const today = getLocalDateString();
    
    if (minDate && minDate > today) {
      // This shouldn't happen in normal circumstances, but handle it
      setOutDate(today);
    } else if (minDate) {
      // If there's a minimum date, use today (which should be >= minDate)
      setOutDate(today);
    } else {
      // No history, use today
      setOutDate(today);
    }
  };

  const handleCloseReelDetails = () => {
    setShowReelDetails(false);
    setSelectedReel(null);
    setOutQuantity("");
    setOutDate(getLocalDateString()); // Reset to today
  };

  // if (!isOpen) return null;


  // Calculate totals for the history tab
  const { totalQuantity, totalPrice, reelCount } = calculateHistoryTotals();

  // Calculate totals for the reel out (using filtered data)
  const calculateReelTotals = () => {
    const filteredReels = filterReelsByDays(reels);
    const uniqueReels = new Set();
    
    const totals = filteredReels.reduce(
      (acc, reel) => {
        uniqueReels.add(reel.reel_number);
        return {
          totalQuantity: acc.totalQuantity + Number(reel.quantity),
          totalPrice: acc.totalPrice + Number(reel.quantity) * Number(reel.rate),
          reelCount: uniqueReels.size
        };
      },
      { totalQuantity: 0, totalPrice: 0, reelCount: 0 }
    );
    
    return totals;
  };

  useEffect(() => {
    fetchReels();
    fetchHistory();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, searchQuery]);
  // Sorting states
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");
  const naturalSortReel = (a, b) => {
    const extractParts = (reel) => {
      const match = reel.match(/([a-zA-Z-]+)(\d+)/);
      return match
        ? { prefix: match[1], number: parseInt(match[2], 10) }
        : { prefix: reel, number: 0 };
    };

    const aParts = extractParts(a);
    const bParts = extractParts(b);

    // First compare prefixes
    if (aParts.prefix !== bParts.prefix) {
      return aParts.prefix.localeCompare(bParts.prefix);
    }

    // Then compare numbers
    return aParts.number - bParts.number;
  };

  // Sorting function
  const processSortedData = (items) => {
    if (!sortColumn) return items;

    return [...items].sort((a, b) => {
      let valueA = a[sortColumn];
      let valueB = b[sortColumn];

      // Handle status sorting (is_partially_used: 0 = Fresh, 1 = Used)
      if (sortColumn === "is_partially_used") {
        valueA = Number(valueA);
        valueB = Number(valueB);
      }
      // Handle date/timestamp sorting
      else if (sortColumn === "out_timestamp") {
        valueA = new Date(valueA).getTime();
        valueB = new Date(valueB).getTime();
      }
      // Handle calculated price sorting
      else if (sortColumn === "price") {
        valueA = Number(a.quantity_used) * Number(a.rate_per_kg);
        valueB = Number(b.quantity_used) * Number(b.rate_per_kg);
      }
      // Handle numeric sorting
      else if (["quantity", "rate", "days", "quantity_used", "quantity_left", "rate_per_kg"].includes(sortColumn)) {
        valueA = Number(valueA);
        valueB = Number(valueB);
      } else if (sortColumn === "reel_number") {
        const sortMultiplier = sortDirection === "asc" ? 1 : -1;
        return sortMultiplier * naturalSortReel(a[sortColumn], b[sortColumn]);
      }
      // Handle string sorting
      else if (typeof valueA === "string") {
        valueA = valueA.toLowerCase();
        valueB = valueB.toLowerCase();
      }

      if (valueA < valueB) return sortDirection === "asc" ? -1 : 1;
      if (valueA > valueB) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
  };

  // Handling sort
  const handleSort = (column) => {
    if (sortColumn === column) {
      // If already sorting this column, toggle direction
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      // If new column, start with ascending
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  // Sorting icon component
  const SortIcon = ({ column }) => {
    if (sortColumn !== column) {
      return <span className="ml-2 text-gray-300">↕️</span>;
    }
    return sortDirection === "asc" ? "⬆️" : "⬇️";
  };

  // Filter reels by days (client-side filtering)
  const filterReelsByDays = (items) => {
    if (!filters.minDays && !filters.maxDays) return items;
    
    return items.filter((item) => {
      const days = Number(item.days);
      const minDays = filters.minDays ? Number(filters.minDays) : null;
      const maxDays = filters.maxDays ? Number(filters.maxDays) : null;
      
      if (minDays !== null && maxDays !== null) {
        return days >= minDays && days <= maxDays;
      } else if (minDays !== null) {
        return days >= minDays;
      } else if (maxDays !== null) {
        return days <= maxDays;
      }
      return true;
    });
  };

  // Pagination and data processing
  const processData = (items) => {
    // First filter by days
    const filteredItems = filterReelsByDays(items);
    
    // Then sort
    const sortedItems = processSortedData(filteredItems);

    // Then paginate
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = sortedItems.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(sortedItems.length / itemsPerPage);

    return { currentItems, totalPages };
  };

  // Process reels and history
  const { currentItems: paginatedReels, totalPages: reelTotalPages } =
    processData(reels);
  const filteredHistory = getFilteredHistory();
  const { currentItems: paginatedHistory, totalPages: historyTotalPages } =
    processData(filteredHistory);
  // Render method (similar to modal, but with full page layout)
  return (
    <div className="paper-out-page">
      <NavigationPaperDashboard />

      {successMessage && (
        <Alert
          type="success"
          message={successMessage}
          onClose={() => setSuccessMessage(null)}
        />
      )}
      {error && (
        <Alert type="error" message={error} onClose={() => setError(null)} />
      )}

      <div className="container mx-auto px-4 py-8">
        <h1 className="text-2xl text-2xl-inventoryOut font-bold mb-6">
          Paper Out Management
        </h1>

        {isLoading && <div className="loading-spinner">Loading...</div>}

        <div className="tabs mb-4">
          <button
            className={`tab-button ${activeTab === "reel-out" ? "active" : ""}`}
            onClick={() => setActiveTab("reel-out")}
          >
            Reel Out
          </button>
          <button
            className={`tab-button ${activeTab === "history" ? "active" : ""}`}
            onClick={() => setActiveTab("history")}
          >
            History
          </button>
        </div>

        {/* Rest of the content from PaperOutModal, adapted for full page */}
        {activeTab === "reel-out" ? (
          <div className="form-content form-content-paperOut">
            {/* <div className="search-container"> */}
            {/* <div class="search-container">
              <input
                type="text"
                placeholder="Search by paper name, shade, reel number..."
                value={searchQuery}
                onChange={handleSearchChange}
                className="search-input"
              />
            </div> */}
            <div className="summary-box">
              <div>
                <div>
                  <strong>Reel Count</strong>
                  <span>
                    {calculateReelTotals().reelCount}
                  </span>
                </div>
                <div>
                  <strong>Total Quantity</strong>
                  <span>
                    {calculateReelTotals().totalQuantity.toFixed(2)} kg
                  </span>
                </div>
                <div>
                  <strong>Total Price</strong>
                  <span>
                    ₹{calculateReelTotals().totalPrice.toFixed(2)}
                  </span>
                </div>
              </div>
            </div>

            <div className="search-grid-container">
              {/* Paper Name */}
              <div>
                <input
                  type="text"
                  placeholder="Search by Paper Name"
                  value={filters.paperName}
                  onChange={(e) =>
                    handleFilterChange("paperName", e.target.value)
                  }
                />
              </div>

              {/* Mill Name */}
              <div>
                <input
                  type="text"
                  placeholder="Search by Mill Name"
                  value={filters.millName}
                  onChange={(e) =>
                    handleFilterChange("millName", e.target.value)
                  }
                />
              </div>

              {/* Shade Name */}
              <div>
                <input
                  type="text"
                  placeholder="Search by Shade Name"
                  value={filters.shadeName}
                  onChange={(e) =>
                    handleFilterChange("shadeName", e.target.value)
                  }
                />
              </div>

              {/* Reel Number */}
              <div>
                <input
                  type="text"
                  placeholder="Search by Reel Number"
                  value={filters.reelNumber}
                  onChange={(e) =>
                    handleFilterChange("reelNumber", e.target.value)
                  }
                />
              </div>

              {/* Days Range */}
              <div className="date-container">
                <input
                  type="number"
                  placeholder="Min Days"
                  value={filters.minDays}
                  onChange={(e) =>
                    handleFilterChange("minDays", e.target.value)
                  }
                  min="0"
                />
                <input
                  type="number"
                  placeholder="Max Days"
                  value={filters.maxDays}
                  onChange={(e) =>
                    handleFilterChange("maxDays", e.target.value)
                  }
                  min={filters.minDays || "0"}
                />
              </div>

              {/* Reset Button */}
              <div>
                <button
                  onClick={handleReset}
                >
                  Reset
                </button>
              </div>
            </div>
            {/* Table and pagination similar to modal version */}
            {/* <div className="table-container"> */}
            {/* <table> */}
            <div className="table-container-paperOut">
              <div className="table-wrapper">
                <table className="table reel-out-table">
                {/* Table headers and rows similar to modal version */}
                <thead>
                  <tr>
                    <th
                      onClick={() => handleSort("is_partially_used")}
                      className="cursor-pointer"
                    >
                      Status <SortIcon column="is_partially_used" />
                    </th>
                    <th
                      onClick={() => handleSort("paper_name")}
                      className="cursor-pointer"
                    >
                      Paper Name (ReelSz/GSM/BF) <SortIcon column="paper_name" />
                    </th>
                    <th
                      onClick={() => handleSort("reel_number")}
                      className="cursor-pointer"
                    >
                      Reel No. <SortIcon column="reel_number" />
                    </th>
                    <th
                      onClick={() => handleSort("quantity")}
                      className="cursor-pointer"
                    >
                      Qty(kg) <SortIcon column="quantity" />
                    </th>
                    <th
                      onClick={() => handleSort("mill_name")}
                      className="cursor-pointer"
                    >
                      Mill Name <SortIcon column="mill_name" />
                    </th>
                    <th
                      onClick={() => handleSort("shade")}
                      className="cursor-pointer"
                    >
                      Shade <SortIcon column="shade" />
                    </th>
                    <th
                      onClick={() => handleSort("rate")}
                      className="cursor-pointer"
                    >
                      Rate(₹) <SortIcon column="rate" />
                    </th>
                    <th
                      onClick={() => handleSort("days")}
                      className="cursor-pointer"
                    >
                      Days <SortIcon column="days" />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedReels.map((reel) => (
                    <tr
                      key={reel.id}
                      onClick={() => handleReelClick(reel)}
                      className="table-row-hover"
                    >
                      {/* Table row content */}
                      <td>
                        {reel.is_partially_used ? (
                          <span className="status used">Used</span>
                        ) : (
                          <span className="status fresh">Fresh</span>
                        )}
                      </td>
                      <td>{reel.paper_name}</td>
                      <td>{reel.reel_number}</td>
                      <td>{reel.quantity}</td>
                      <td>{reel.mill_name}</td>
                      <td>{reel.shade}</td>
                      <td>{reel.rate}</td>
                      <td>{reel.days}</td>
                    </tr>
                  ))}
                </tbody>
                </table>
              </div>
              <div className="pagination-container">
                <button
                  className="pagination-button"
                  onClick={goToPreviousPage}
                  disabled={currentPage === 1}
                >
                  ‹
                </button>
                <div className="pagination-info">
                  Page <strong>{currentPage}</strong> of{" "}
                  <strong>{reelTotalPages || 1}</strong>
                </div>
                <button
                  className="pagination-button"
                  onClick={goToNextPage}
                  disabled={currentPage === reelTotalPages}
                >
                  ›
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="form-content form-content-paperOut">
            {/* History tab content similar to modal version */}
            <div className="summary-box">
              <div>
                <div>
                  <strong>Reel Count</strong>
                  <span>
                    {reelCount}
                  </span>
                </div>
                <div>
                  <strong>Total Quantity</strong>
                  <span>
                    {totalQuantity.toFixed(2)} kg
                  </span>
                </div>
                <div>
                  <strong>Total Price</strong>
                  <span>
                    ₹{totalPrice.toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
            
            {/* Date Filter for History */}
            <div className="search-grid-container">
              <div className="date-container">
                <input
                  type="date"
                  placeholder="Start Date"
                  value={historyDateFilters.startDate}
                  onChange={(e) =>
                    handleHistoryDateFilterChange("startDate", e.target.value)
                  }
                />
                <input
                  type="date"
                  placeholder="End Date"
                  value={historyDateFilters.endDate}
                  onChange={(e) =>
                    handleHistoryDateFilterChange("endDate", e.target.value)
                  }
                />
              </div>

              {/* Reset Button */}
              <div>
                <button onClick={resetHistoryDateFilters}>Reset</button>
              </div>
            </div>
            
            <div className="table-container-paperOut">
              <div className="table-wrapper">
                <table className="history-table">
              <thead>
                <tr>
                  <th
                    onClick={() => handleSort("out_timestamp")}
                    className="cursor-pointer"
                  >
                    Date <SortIcon column="out_timestamp" />
                  </th>
                  <th
                    onClick={() => handleSort("reel_number")}
                    className="cursor-pointer"
                  >
                    Reel No. <SortIcon column="reel_number" />
                  </th>
                  <th
                    onClick={() => handleSort("paper_name")}
                    className="cursor-pointer"
                  >
                    Paper Name (ReelSz/GSM/BF) <SortIcon column="paper_name" />
                  </th>
                  <th
                    onClick={() => handleSort("quantity_used")}
                    className="cursor-pointer"
                  >
                    Qty Usd(kg) <SortIcon column="quantity_used" />
                  </th>
                  <th
                    onClick={() => handleSort("quantity_left")}
                    className="cursor-pointer"
                  >
                    Qty Lft(kg)<SortIcon column="quantity_left" />
                  </th>
                  <th
                    onClick={() => handleSort("mill_name")}
                    className="cursor-pointer"
                  >
                    Mill Name <SortIcon column="mill_name" />
                  </th>
                  <th
                    onClick={() => handleSort("shade")}
                    className="cursor-pointer"
                  >
                    Shade <SortIcon column="shade" />
                  </th>
                  <th
                    onClick={() => handleSort("rate_per_kg")}
                    className="cursor-pointer"
                  >
                   Rate/kg(₹) <SortIcon column="rate_per_kg" />
                  </th>
                  <th>Remark</th>
                  <th
                    onClick={() => handleSort("price")}
                    className="cursor-pointer"
                  >
                    Price(₹) <SortIcon column="price" />
                  </th>
                </tr>
              </thead>
              <tbody>
                {paginatedHistory.map((entry) => {
                  const price = Number(entry.quantity_used) * Number(entry.rate_per_kg);
                  const date = new Date(entry.out_timestamp);
                  const formattedDate = `${String(date.getDate()).padStart(2, '0')}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getFullYear()).slice(-2)}`;
                  return (
                    <tr key={entry.id}>
                      <td>{formattedDate}</td>
                      <td>{entry.reel_number}</td>
                      <td>{entry.paper_name}</td>
                      <td>{entry.quantity_used}</td>
                      <td>{entry.quantity_left}</td>
                      <td>{entry.mill_name}</td>
                      <td>{entry.shade}</td>
                      <td>{entry.rate_per_kg}</td>
                      <td>{entry.remark || '-'}</td>
                      <td>{price.toFixed(2)}</td>
                    </tr>
                  );
                })}
              </tbody>
                </table>
              </div>
              <div className="pagination-container">
              <button
                className="pagination-button"
                onClick={goToPreviousPage}
                disabled={currentPage === 1}
              >
                ‹
              </button>
              <div className="pagination-info">
                Page <strong>{currentPage}</strong> of{" "}
                <strong>{historyTotalPages || 1}</strong>
              </div>
              <button
                className="pagination-button"
                onClick={goToNextPage}
                disabled={currentPage === historyTotalPages}
              >
                ›
              </button>
            </div>
            </div>
          </div>
        )}
      </div>

      {/* Reel Out Sub-Modal (moved outside container for proper overlay) */}
      {showReelDetails && selectedReel && (
        <div className="reel-details-overlay">
          <div className="reel-details-modal">
            <div className="reel-details-header">
              <h3>Reel Details</h3>
              <button
                className="close-button-paperOut"
                onClick={handleCloseReelDetails}
              >
                &times;
              </button>
            </div>

            <div className="reel-details-content">
              <div className="reel-info-grid">
                <div className="reel-info-item">
                  <label>Paper Name</label>
                  <span>{selectedReel.paper_name}</span>
                </div>
                <div className="reel-info-item">
                  <label>Reel Number</label>
                  <span>{selectedReel.reel_number}</span>
                </div>
                <div className="reel-info-item">
                  <label>Mill Name</label>
                  <span>{selectedReel.mill_name}</span>
                </div>
                <div className="reel-info-item">
                  <label>Shade</label>
                  <span>{selectedReel.shade}</span>
                </div>
                <div className="reel-info-item">
                  <label>Current Quantity</label>
                  <span>{selectedReel.quantity} kg</span>
                </div>
                <div className="reel-info-item">
                  <label>Rate</label>
                  <span>₹{selectedReel.rate}</span>
                </div>
                <div className="reel-info-item">
                  <label>Days in Stock</label>
                  <span>{selectedReel.days}</span>
                </div>
                <div className="reel-info-item">
                  <label>Status</label>
                  <span
                    className={`status ${
                      selectedReel.is_partially_used ? "used" : "fresh"
                    }`}
                  >
                    {selectedReel.is_partially_used ? "Used" : "Fresh"}
                  </span>
                </div>
              </div>

              <div className="reel-out-form">
                <h4>Stock Out</h4>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="outQuantity">Out Quantity (kg)</label>
                    <input
                      type="number"
                      id="outQuantity"
                      value={outQuantity}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value >= 0) {
                          setOutQuantity(value);
                        }
                      }}
                      max={selectedReel.quantity}
                      min="0"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="balance">Balance (kg)</label>
                    <input
                      type="number"
                      id="balance"
                      value={
                        outQuantity
                          ? selectedReel.quantity - Number(outQuantity)
                          : selectedReel.quantity
                      }
                      disabled
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="outDate">Stock Out Date</label>
                    <input
                      type="date"
                      id="outDate"
                      value={outDate}
                      onChange={(e) => setOutDate(e.target.value)}
                      min={getMinDateForReel(selectedReel.reel_number) || undefined}
                      max={getLocalDateString()}
                      title={getMinDateForReel(selectedReel.reel_number) 
                        ? `Last stock out: ${new Date(getMinDateForReel(selectedReel.reel_number)).toLocaleDateString('en-GB')}. Select from this date onwards (same day allowed)`
                        : "Select stock out date"}
                    />
                    {getMinDateForReel(selectedReel.reel_number) && (
                      <small style={{ color: '#6b7280', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                        Last stock out: {new Date(getMinDateForReel(selectedReel.reel_number)).toLocaleDateString('en-GB')} (same day or later allowed)
                      </small>
                    )}
                  </div>
                </div>

                <div className="button-group">
                  {/* <button
                    className="cancel-button"
                    onClick={handleCloseReelDetails}
                  >
                    Cancel
                  </button> */}
                  <button
                    className="submit-button"
                    onClick={handleOutQuantitySubmit}
                    disabled={
                      !outQuantity ||
                      Number(outQuantity) > selectedReel.quantity ||
                      isLoading
                    }
                  >
                    {isLoading ? "Processing..." : "Stock Out"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaperOutPage;
